import { useMemo, useState } from "react";

import {
    Column,
    OutlinedCard,
    Row,
    TextSkeleton,
    ToggleButtons,
    ToggleSpacedButtons,
    Text,
    ToggleChevron,
    TableColumn,
    createRows,
    Table,
    ShadowCard,
    TextExternalLink
} from "@bridgesplit/ui";
import {
    formatPercent,
    ALLOWED_FEATURES,
    formatDate,
    formatTokenAmount,
    DEFAULT_SOLANA_EXPLORER
} from "@bridgesplit/utils";
import { AppDialog, useAppDialog } from "app/utils";
import { Collapse, Divider, useMediaQuery } from "@mui/material";
import { Settings } from "@mui/icons-material";
import { COPY } from "app/constants";
import { useActiveWallet, ParsedLendVaultAction, VaultActionType } from "@bridgesplit/abf-react";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { VAULT_DETAIL_QUERY } from "./constants";
import { VaultDeposit } from "./deposit";
import { VaultWithdraw } from "./withdraw";
import { SettingsButton } from "../common";
import { useVaultContext } from "./VaultContext";
import { VaultDepositCard } from "./VaultDepositCard";
import { VaultExternalRewards, VaultExternalRewardsHeader } from "./external-rewards";

enum Open {
    Deposit,
    Withdraw
}

export function VaultCard() {
    const isMobile = useMediaQuery(VAULT_DETAIL_QUERY.below);
    const [open, setOpen] = useState<Open | undefined>(Open.Deposit);

    if (isMobile) {
        return <VaultCardMobile open={open} setOpen={setOpen} />;
    }

    return (
        <Column spacing={3}>
            <Column spacing={1}>
                <VaultDepositCard />
                <OutlinedCard hideCard={isMobile} spacing={3}>
                    <Row spaceBetween>
                        <ToggleSpacedButtons
                            variant="default"
                            size="small"
                            value={open}
                            setValue={setOpen}
                            options={[
                                {
                                    label: COPY.VAULT_DEPOSIT_CTA,
                                    value: Open.Deposit
                                },
                                { label: COPY.VAULT_WITHDRAW_CTA, value: Open.Withdraw }
                            ]}
                        />
                        {open === Open.Withdraw && ALLOWED_FEATURES.showVaultWithdrawSlippage && (
                            <SlippageSettingButton />
                        )}
                    </Row>
                    {open === Open.Deposit && <VaultDeposit />}
                    {open === Open.Withdraw && <VaultWithdraw />}
                </OutlinedCard>
                <VaultExternalRewardsCard />
                <VaultDepositSummaryCard />
            </Column>
        </Column>
    );
}

function VaultCardMobile({ open, setOpen }: { open: Open | undefined; setOpen: (open: Open) => void }) {
    return (
        <OutlinedCard spacing={3} hideCard>
            <VaultDepositCard />

            <ToggleButtons
                value={open}
                setValue={setOpen}
                options={[
                    {
                        value: Open.Deposit,
                        label: COPY.VAULT_DEPOSIT_CTA
                    },
                    { value: Open.Withdraw, label: COPY.VAULT_WITHDRAW_CTA }
                ]}
            />
            {open === Open.Withdraw && ALLOWED_FEATURES.showVaultWithdrawSlippage && <SlippageSettingButton />}
            {open === Open.Deposit && <VaultDeposit />}
            {open === Open.Withdraw && <VaultWithdraw />}
            <VaultExternalRewardsCard />
        </OutlinedCard>
    );
}

function SlippageSettingButton() {
    const { open } = useAppDialog();
    const { slippageSetting, setSlippageSetting } = useVaultContext();

    if (slippageSetting === undefined) return <TextSkeleton variant="caption" width="30px" />;

    return (
        <SettingsButton
            onClick={() => {
                open(AppDialog.VaultWithdrawSlippage, { slippageSetting, setSlippageSetting });
            }}
        >
            <Settings />

            {formatPercent(slippageSetting, { minimumSignificantDigits: 1 })}
        </SettingsButton>
    );
}

function VaultExternalRewardsCard() {
    const { activeWallet } = useActiveWallet();
    const { userExternalRewardsPosition } = useVaultContext();
    if (!activeWallet || userExternalRewardsPosition === null) return null;

    return (
        <OutlinedCard spacing={2}>
            <VaultExternalRewardsHeader />
            <Divider />
            <VaultExternalRewards />
        </OutlinedCard>
    );
}

export type ParsedLendVaultActionWithContribution = ParsedLendVaultAction & { contribution: number };

function calculateContributionsFromActions(
    vaultActions: ParsedLendVaultAction[]
): ParsedLendVaultActionWithContribution[] {
    let runningContribution = 0;

    return vaultActions.map((vaultAction) => {
        const isWithdrawal = vaultAction.actionType === VaultActionType.Withdrawal;
        runningContribution += isWithdrawal ? -vaultAction.amount : vaultAction.amount;

        return { ...vaultAction, contribution: runningContribution };
    });
}

function VaultDepositSummaryTable() {
    const { vaultDepositSummary, vaultExpanded } = useVaultContext();

    const vaultDepositActions = useMemo(() => {
        return calculateContributionsFromActions([...(vaultDepositSummary?.vaultActions ?? [])]).reverse();
    }, [vaultDepositSummary?.vaultActions]);

    const columns = getVaultDepositSummaryTableColumns(vaultExpanded?.principalMetadata);

    return (
        <Table
            paginationProps={{
                pageSize: 5,
                type: "managed",
                defaultSortKey: "allocation"
            }}
            emptyPlaceholder="No Deposit History"
            loading={!vaultDepositActions}
            rows={createRows(vaultDepositActions, (s) => s.timestamp)}
            columns={columns}
        />
    );
}

function getVaultDepositSummaryTableColumns(
    metadata: TokenListMetadata | undefined
): TableColumn<ParsedLendVaultActionWithContribution>[] {
    return [
        {
            dataIndex: "date",
            title: "Date",
            render: ({ data }) => (
                <Row spacing={1}>
                    <Text>{formatDate(data.timestamp, "date", { showTime: "never" })}</Text>
                </Row>
            ),
            sortBy: ({ data }) => data.timestamp,
            width: "30%",
            align: "left"
        },
        {
            dataIndex: "transaction",
            title: "Transaction",
            render: ({ data }) => {
                return (
                    <Row spacing={1}>
                        <TextExternalLink
                            underlineLink
                            href={`${DEFAULT_SOLANA_EXPLORER}tx/${data.transactionSignature}`}
                        >
                            {data.actionType === VaultActionType.Withdrawal ? "-" : "+"}
                            {formatTokenAmount(data.amount, {
                                symbol: metadata?.symbol,
                                decimals: metadata?.decimals,
                                compactLargeNumbers: true,
                                short: true
                            })}
                        </TextExternalLink>
                    </Row>
                );
            },
            width: "50%"
        },
        {
            dataIndex: "contribution",
            title: COPY.NET_DEPOSITS,
            render: ({ data }) => {
                const netContribution = data.contribution;
                // negative contribution should be equivalant to profit
                const color = data.contribution < 0 ? "success" : undefined;

                return (
                    <Text color={color}>
                        {formatTokenAmount(netContribution, {
                            symbol: metadata?.symbol,
                            decimals: metadata?.decimals,
                            compactLargeNumbers: true,
                            short: true
                        })}
                    </Text>
                );
            },
            width: "20%",
            align: "right"
        }
    ];
}

function VaultDepositSummaryCard() {
    const [expanded, setExpanded] = useState(false);
    const { vaultDepositSummary } = useVaultContext();
    const depositActions = vaultDepositSummary?.vaultActions ?? [];

    if (depositActions.length === 0) return null;

    return (
        <ShadowCard>
            <Column sx={{ p: 2, cursor: "pointer" }} onClick={() => setExpanded((prev) => !prev)}>
                <Row spaceBetween>
                    <Text variant="h4">Deposit Summary</Text>
                    <ToggleChevron textVariant="h4" expanded={expanded} />
                </Row>
            </Column>
            <Collapse in={expanded}>
                <Column sx={{ p: 2, pt: 0 }} spacing={2}>
                    <VaultDepositSummaryTable />
                </Column>
            </Collapse>
        </ShadowCard>
    );
}
